import type { ComponentType } from "react";

import { BreadcrumbHeader, type IconWithSizeProps } from "@stacklet/ui";
import { useFragment } from "react-relay";
import { useLocation } from "react-router";
import { graphql } from "relay-runtime";

import { useConfig } from "app/contexts";
import { useDocsUrl } from "app/hooks/config/useDocsUrl/useDocsUrl";

import type { BreadCrumbsHeader_query$key } from "./__generated__/BreadCrumbsHeader_query.graphql";

export interface BreadCrumbElement {
  label?: string;
  target?: string;
  tabs?: {
    label: string;
    target: string;
    linkTarget?: string;
    /** Optional chip text shown next to the label in the tab rail */
    chip?: string;
    /** Optional section heading grouping this tab in the settings rail */
    section?: string;
    /** Optional leading icon shown next to the label in the tab rail */
    icon?: ComponentType<IconWithSizeProps>;
    /**
     * Starts the tab's page's queries when its rail link is hovered, so the
     * page opens without a wait. See `usePreloadIntent`.
     */
    onPreload?: () => void;
  }[];
}

interface Props {
  queryRef: BreadCrumbsHeader_query$key;
  breadcrumbs: BreadCrumbElement[];
  currentPageTitle?: string;
}

export function BreadCrumbsHeader({
  queryRef,
  breadcrumbs,
  currentPageTitle,
}: Props) {
  const config = useConfig();
  const supportEnabled = !!config && !config.is_poc;
  const location = useLocation();

  const data = useFragment(
    graphql`
      fragment BreadCrumbsHeader_query on Query {
        whoami {
          email
          displayName
        }
        ...useDocsUrl_query
      }
    `,
    queryRef,
  );

  const { displayName, email } = data.whoami ?? {};
  const getDocsUrl = useDocsUrl(data);

  const baseCrumbs = breadcrumbs.flatMap((crumb) => {
    if (Array.isArray(crumb.tabs)) {
      const activeTab = crumb.tabs.find(
        (t) => t.target && location.pathname.startsWith(t.target),
      );
      return activeTab
        ? [
            {
              label: activeTab.label,
              target: activeTab.linkTarget ?? activeTab.target,
            },
          ]
        : [];
    }
    if (crumb.label) {
      return [{ label: crumb.label, target: crumb.target }];
    }
    return [];
  });

  const lastCrumb = baseCrumbs.at(-1);
  // Append the page title as the trailing crumb unless it would duplicate the
  // last crumb (same label, or the last crumb already links to this exact path).
  // A target-less or empty trail must not drop the title.
  const showCurrentPageCrumb =
    !!currentPageTitle &&
    currentPageTitle !== lastCrumb?.label &&
    lastCrumb?.target !== location.pathname;

  const v2Breadcrumbs = showCurrentPageCrumb
    ? [...baseCrumbs, { label: currentPageTitle }]
    : baseCrumbs;

  return (
    <div className="sticky top-0 z-10 mb-xl mt-xs">
      <BreadcrumbHeader
        breadcrumbs={v2Breadcrumbs}
        docsUrl={getDocsUrl("/") ?? ""}
        email={email ?? ""}
        pathname={location.pathname}
        supportEnabled={supportEnabled}
        username={displayName || email || ""}
      />
    </div>
  );
}
